/**
 * @author Paul Allen
 * @created 4-20-2019
 * @name Lamplighter
 * @description This a light debug library that allows you to log information to the console
 * but only if they debug flag is set
 *
 * Usuage:
 * -- Import the package --
 * import debug from 'path/to/service'
 *
 * -- Configure at the start of app --
 * debug.configure({key: 'key-for-localStorage'})
 *
 * -- Use in code --
 * // Almost All console functions are supported
 * debug.log("MY Message")
 * debug.warn("My Warning")
 * ... others
 *
 * -- Force --
 * // you can force a message to show even when debug is not enabled
 * debug.log({force: true}, "My Forced Message")
 *
 *
 * // TODO:
 * - [] Add support for color customizations
 * - [] Make a published npm pacakge
 * - [] support a configuration to expect other queryParam flags
 *
 */

import { getQueryParamsFromUrl } from '../utils'

/**
 * This function set the state of the debug
 * @param {Boolean} state Is Debug on or off
 * @param {String} key the key in local storage that stores the state
 */
const setDebugflag = (state, key) => {
  return state === false
    ? window.localStorage.removeItem(key)
    : window.localStorage.setItem(key, state)
}

/**
 * This function checks to see if a param  of "Debug is set"
 */
const checkParamFlag = () => {
  const params = getQueryParamsFromUrl(window.location.href)
  if (params.debug === 'true' || params.debug === '1') return true
  if (params.debug === 'false' || params.debug === '0') return false
  return null
}

/**
 * Store the string of the localStorage key
 * @param {String} key
 */
const setKey = key => {
  window.debugKey = key
}

/**
 * Check the debug status from both the param and localStorage
 * @param {String} key
 */
const checkDebugStatus = key => {
  if (
    window.env !== 'local' &&
    window.env !== 'dev' &&
    window.env !== 'qa' &&
    window.env !== 'qa-alt1' &&
    window.env !== 'qa-alt2' &&
    window.env !== 'qa-alt3' &&
    window.env !== 'qa-alt4' &&
    window.env !== 'qa-alt5'
  )
    return setDebugflag(false, key)
  window.lampLighter = () =>
    'The Cones of Dunshire is the ninth-highest-selling multi-player figurine-based strategy fantasy sequel game in history'
  if (checkParamFlag() === false) return setDebugflag(false, key)
  return setDebugflag(true, key)
}

/**
 * Check to see if debug is enabled
 */
const isDebugEnabled = () => {
  return window.localStorage.getItem(window.debugKey) === 'true'
}

/**
 * these functions look at the type of message coming in and use the correspoding
 * console function ex. log --> console.log
 * @param  {...any} messages // arguments to pass into the console function
 */
const log = (...messages) => logFunc({ type: 'log' }, ...messages)
const warn = (...messages) => logFunc({ type: 'warn' }, ...messages)
const table = (...messages) => logFunc({ type: 'table' }, ...messages)
const group = (...messages) => logFunc({ type: 'group' }, ...messages)
const error = (...messages) => logFunc({ type: 'error' }, ...messages)
const groupEnd = (...messages) => logFunc({ type: 'groupEnd' }, ...messages)
const info = (...messages) => logFunc({ type: 'info' }, ...messages)
const groupCollapsed = (...messages) =>
  logFunc({ type: 'groupCollapsed' }, ...messages)
const time = (...messages) => logFunc({ type: 'time' }, ...messages)
const timeEnd = (...messages) => logFunc({ type: 'timeEnd' }, ...messages)
const timeLog = (...messages) => logFunc({ type: 'timeLog' }, ...messages)
const trace = (...messages) => logFunc({ type: 'trace' }, ...messages)
const clear = (...messages) => logFunc({ type: 'clear' }, ...messages)
const count = (...messages) => logFunc({ type: 'count' }, ...messages)
const countReset = (...messages) => logFunc({ type: 'countReset' }, ...messages)

/**
 * This is the main log function that will only fire if debug is enabled or
 * if the force flag is thrown through the first object
 */
const logFunc = ({ type = 'log' } = {}, ...messages) => {
  if (messages.length === 0) return console[type]()
  if (messages[0].force) {
    return console[type](...messages.slice(1))
  }
  if (!isDebugEnabled() && !messages[0].force) return
  if (messages) console[type](...messages)
}

export default {
  configure({ key = 'lampLighter' }) {
    setKey(key)
    checkDebugStatus(key)
  },
  log,
  warn,
  table,
  error,
  group,
  groupEnd,
  info,
  groupCollapsed,
  time,
  timeEnd,
  timeLog,
  trace,
  clear,
  count,
  countReset,
  setDebugflag
}
